import { memo } from 'react';
import { useInView } from 'react-intersection-observer';

import { cn } from '@air/utils-tailwind';

import { PrivateWorkspaceAgentPanelMessageSkillMonikerTooltip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageSkillMonikerTooltip';
import {
  usePrivateWorkspaceAgentSkillMonikerLabel,
  type WorkspaceAgentSkillMonikerLabelState,
} from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentSkillMonikerLabel';
import { usePrivateWorkspaceAgentSkillsCatalog } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentSkillsCatalogProvider';

const formatWorkspaceAgentSkillMonikerDisplay = (state: WorkspaceAgentSkillMonikerLabelState): string => {
  if (state.status === 'resolved') {
    return `/${state.slashCommand}`;
  }

  if (state.status === 'pending') {
    return '…';
  }

  return 'Unavailable skill';
};

export type PrivateWorkspaceAgentPanelMessageSkillMonikerProps = {
  skillId: string;
};

export const PrivateWorkspaceAgentPanelMessageSkillMoniker = memo(
  ({ skillId }: PrivateWorkspaceAgentPanelMessageSkillMonikerProps) => {
    const { isSkillsCatalogEnabled } = usePrivateWorkspaceAgentSkillsCatalog();
    const { inView, ref } = useInView({ triggerOnce: true });

    const { labelState } = usePrivateWorkspaceAgentSkillMonikerLabel({
      enabled: isSkillsCatalogEnabled && inView,
      skillId,
    });

    const display = formatWorkspaceAgentSkillMonikerDisplay(labelState);
    const isResolved = labelState.status === 'resolved';
    const text = (
      <span
        className={cn(isResolved && 'rounded-2 outline-none focus-visible:ring-2 focus-visible:ring-altitude-9')}
        data-status={labelState.status}
        data-testid="WORKSPACE_AGENT_MESSAGE_SKILL_MONIKER"
        ref={ref}
        tabIndex={isResolved ? 0 : undefined}
      >
        {display}
      </span>
    );

    if (!isResolved) {
      return text;
    }

    return (
      <PrivateWorkspaceAgentPanelMessageSkillMonikerTooltip description={labelState.description} name={labelState.name}>
        {text}
      </PrivateWorkspaceAgentPanelMessageSkillMonikerTooltip>
    );
  },
);

PrivateWorkspaceAgentPanelMessageSkillMoniker.displayName = 'PrivateWorkspaceAgentPanelMessageSkillMoniker';
